import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "./components/PageHeader.js";
import SiteFooter from "./components/SiteFooter.js";
import { hasMcpToolPack } from "./plugins/appPlugins.js";
import "./HomePage.css";
import "./Apps.css";

const API_BASE = process.env.REACT_APP_API_URL || "";

// Brand logos: Commons / vivid SVGs first, then colored Simple Icons CDN, then
// Google s2 favicons. Never prefer jsDelivr mono SVGs (black/gray).
// Note: cdn.simpleicons.org 404s for several TM brands (Slack, Canva, Outlook, …).
function brandFavicon(domain) {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;
}

const APP_LOGO_LOCAL = {
  // Adobe Creative Cloud (multi-color Commons SVGs)
  photoshop: "https://upload.wikimedia.org/wikipedia/commons/a/af/Adobe_Photoshop_CC_icon.svg",
  illustrator: "https://upload.wikimedia.org/wikipedia/commons/f/fb/Adobe_Illustrator_CC_icon.svg",
  lightroom: "https://upload.wikimedia.org/wikipedia/commons/5/56/Adobe_Photoshop_Lightroom_Classic_CC_icon.svg",
  premiere: "https://upload.wikimedia.org/wikipedia/commons/4/40/Adobe_Premiere_Pro_CC_icon.svg",
  indesign: "https://upload.wikimedia.org/wikipedia/commons/4/48/Adobe_InDesign_CC_icon.svg",
  "adobe-xd": "https://upload.wikimedia.org/wikipedia/commons/c/c2/Adobe_XD_CC_icon.svg",
  // Vivid multi-color Commons marks (preferred over single-fill Simple Icons / dull favicons)
  gmail: "https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg",
  slack: "https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg",
  "google-calendar": "https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg",
  // Brand favicons for apps where colored CDN 404s or mark is multi-color
  "google-drive": brandFavicon("drive.google.com"),
  "google-workspace": brandFavicon("workspace.google.com"),
  "google-maps": brandFavicon("maps.google.com"),
  "google-meet": brandFavicon("meet.google.com"),
  "google-ai": brandFavicon("gemini.google.com"),
  "google-ads": brandFavicon("ads.google.com"),
  "google-analytics": brandFavicon("analytics.google.com"),
  "google-cloud": brandFavicon("cloud.google.com"),
  instagram: brandFavicon("instagram.com"),
  youtube: brandFavicon("youtube.com"),
  "youtube-music": brandFavicon("music.youtube.com"),
  outlook: brandFavicon("outlook.live.com"),
  teams: brandFavicon("teams.microsoft.com"),
  "microsoft-365": brandFavicon("www.microsoft365.com"),
  "microsoft-to-do": brandFavicon("to-do.office.com"),
  onenote: brandFavicon("onenote.com"),
  linkedin: brandFavicon("linkedin.com"),
  canva: brandFavicon("canva.com"),
  facebook: brandFavicon("facebook.com"),
  "meta-ads": brandFavicon("business.facebook.com"),
  openai: brandFavicon("openai.com"),
  salesforce: brandFavicon("salesforce.com"),
  twilio: brandFavicon("twilio.com"),
  "nano-banana":
    "data:image/svg+xml;utf8," +
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 256'>" +
    "<circle cx='128' cy='128' r='115' fill='%23F5D547'/>" +
    "<path d='M100 80 Q140 60 180 90 Q160 140 130 180 Q90 160 70 120 Q80 90 100 80Z' fill='%23F4A726'/>" +
    "<text x='128' y='155' font-family='sans-serif' font-size='56' font-weight='bold' fill='%23333' text-anchor='middle'>NB</text>" +
    "</svg>",
  "veo3":
    "data:image/svg+xml;utf8," +
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 256'>" +
    "<rect width='256' height='256' rx='48' fill='%231a1a2e'/>" +
    "<path d='M100 70 L100 186 L180 128 Z' fill='%23e53935'/>" +
    "<text x='128' y='220' font-family='sans-serif' font-size='32' font-weight='bold' fill='%23fff' text-anchor='middle'>VO3</text>" +
    "</svg>",
  lovable:
    "data:image/svg+xml;utf8," +
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 256'>" +
    "<rect width='256' height='256' rx='56' fill='%23FF6B6B'/>" +
    "<path d='M128 196 C88 164 56 136 56 100 C56 74 76 56 100 56 C114 56 124 64 128 74 C132 64 142 56 156 56 C180 56 200 74 200 100 C200 136 168 164 128 196Z' fill='%23fff'/>" +
    "</svg>",
};
const APP_LOGO_ICONS = {
  "adobe-xd": "adobexd", indesign: "adobeindesign",
  canva: "canva", figma: "figma", miro: "miro", spotify: "spotify", "apple-music": "applemusic",
  "youtube-music": "youtube", soundcloud: "soundcloud", deezer: "deezer", tidal: "tidal",
  tripadvisor: "tripadvisor", booking: "bookingdotcom", airbnb: "airbnb", "google-maps": "googlemaps",
  expedia: "expedia", skyscanner: "skyscanner", "google-workspace": "google", "microsoft-365": "microsoft",
  notion: "notion", slack: "slack", trello: "trello", asana: "asana", monday: "mondaydotcom",
  airtable: "airtable", dropbox: "dropbox", todoist: "todoist", clickup: "clickup", basecamp: "basecamp",
  instagram: "instagram", facebook: "facebook", twitter: "x", linkedin: "linkedin", tiktok: "tiktok",
  pinterest: "pinterest", reddit: "reddit", twitch: "twitch", snapchat: "snapchat", gmail: "gmail",
  outlook: "microsoftoutlook", zoom: "zoom", teams: "microsoftteams", discord: "discord", telegram: "telegram",
  twilio: "twilio", "whatsapp-business": "whatsapp", vercel: "vercel", github: "github", gitlab: "gitlab",
  bitbucket: "bitbucket", netlify: "netlify", supabase: "supabase", firebase: "firebase", stripe: "stripe",
  paypal: "paypal", square: "square", mailchimp: "mailchimp", sendgrid: "sendgrid", hubspot: "hubspot",
  salesforce: "salesforce", zendesk: "zendesk", intercom: "intercom", calendly: "calendly", docusign: "docusign",
  pandora: "pandora", "amazon-music": "amazon", sketch: "sketch", vimeo: "vimeo", medium: "medium",
  "google-drive": "googledrive", box: "box", linear: "linear", youtube: "youtube", aws: "amazonaws",
  "google-cloud": "googlecloud", azure: "microsoftazure", heroku: "heroku", digitalocean: "digitalocean",
  mongodb: "mongodb", docker: "docker", wise: "wise", openai: "openai", anthropic: "anthropic",
  huggingface: "huggingface", webex: "cisco", buffer: "buffer", hootsuite: "hootsuite",
  shopify: "shopify", woocommerce: "woocommerce", bigcommerce: "bigcommerce", zapier: "zapier",
  "google-analytics": "googleanalytics", segment: "segment", mixpanel: "mixpanel", amplitude: "amplitude",
  wix: "wix", wordpress: "wordpress", webflow: "webflow", contentful: "contentful", sanity: "sanity",
  "google-calendar": "googlecalendar", cloudflare: "cloudflare", auth0: "auth0", jira: "jira",
  "ticktick": "ticktick", "coda": "coda", "obsidian": "obsidian",
  confluence: "confluence", pagerduty: "pagerduty", datadog: "datadog", loom: "loom", mux: "mux",
  quickbooks: "quickbooks", xero: "xero", coda: "coda", klaviyo: "klaviyo", convertkit: "convertkit",
  braze: "braze", framer: "framer", typeform: "typeform", "meta-ads": "meta", "google-ads": "googleads", "google-ai": "google",
  make: "make", n8n: "n8n", "square-commerce": "square", freshbooks: "freshbooks",
  replit: "replit", etsy: "etsy", gumroad: "gumroad", mattermost: "mattermost", ifttt: "ifttt",
  paddle: "paddle", dribbble: "dribbble", behance: "behance", alltrails: "alltrails", zillow: "zillow",
  feishu: "lark", slite: "slite", craft: "craft", copper: "copper", pipedrive: "pipedrive",
  twist: "twist", mastodon: "mastodon", bluesky: "bluesky", substack: "substack",
  raindrop: "raindropio", pocket: "getpocket", nuclino: "nuclino", "microsoft-to-do": "microsoft",
  readwise: "readwise", "rocket-chat": "rocketdotchat", zulip: "zulip", whereby: "whereby",
  render: "render", railway: "railway", fly: "flyio", circleci: "circleci", sentry: "sentry",
  "terraform-cloud": "terraform", ebay: "ebay", ecwid: "ecwid", bigcartel: "bigcartel",
  mercadolibre: "mercadolibre", ghost: "ghost", "dev-to": "devdotto", hashnode: "hashnode",
  tumblr: "tumblr", patreon: "patreon", "ko-fi": "kofi", activecampaign: "activecampaign",
  getresponse: "getresponse", "customer-io": "customerio", brevo: "brevo", resend: "resend",
  mollie: "mollie", gocardless: "gocardless", razorpay: "razorpay", viator: "viator",
  uber: "uber", lyft: "lyft", cohere: "cohere", mistral: "mistralai", stability: "stability",
  replicate: "replicate", elevenlabs: "elevenlabs", assemblyai: "assemblyai", pinecone: "pinecone",
  // Previously domain-only / missing marks
  adyen: "adyen", braintree: "braintree", evernote: "evernote", quora: "quora", signal: "signal",
  smartsheet: "smartsheet", wrike: "wrike", "sprout-social": "sproutsocial",
  onenote: "microsoftonenote", "google-meet": "googlemeet",
};
// Fallback: Clearbit by domain when Simple Icons has no slug (also used for Photoshop/Illustrator – Adobe logo)
const APP_LOGO_DOMAINS = {
  photoshop: "adobe.com", illustrator: "adobe.com",
  kayak: "kayak.com", "hotels-com": "hotels.com", lovable: "lovable.dev", invision: "invisionapp.com",
  lightroom: "adobe.com", premiere: "adobe.com", hopper: "hopper.com", agoda: "agoda.com",
  evernote: "evernote.com", smartsheet: "smartsheet.com", wrike: "wrike.com",
  quora: "quora.com", signal: "signal.org", braintree: "braintree.com",
  adyen: "adyen.com", "sprout-social": "sproutsocial.com",
  zillow: "zillow.com", slite: "slite.com", craft: "craft.do", copper: "copper.com", feishu: "lark.com",
  readwise: "readwise.io", nuclino: "nuclino.com", whereby: "whereby.com", railway: "railway.app",
  fly: "fly.io", "terraform-cloud": "hashicorp.com", ecwid: "ecwid.com", mercadolibre: "mercadolibre.com",
  hashnode: "hashnode.com", "customer-io": "customer.io", resend: "resend.com", assemblyai: "assemblyai.com",
  skyscanner: "skyscanner.net", docusign: "docusign.com", monday: "monday.com",
  // Full-color favicon hosts (also used when APP_LOGO_LOCAL fails)
  slack: "slack.com", linkedin: "linkedin.com", openai: "openai.com", salesforce: "salesforce.com",
  twilio: "twilio.com", sendgrid: "sendgrid.com", segment: "segment.com", amplitude: "amplitude.com",
  heroku: "heroku.com", teams: "teams.microsoft.com", "microsoft-365": "microsoft.com",
  "amazon-music": "music.amazon.com", aws: "aws.amazon.com", azure: "azure.microsoft.com",
  gmail: "gmail.com", "google-calendar": "calendar.google.com", "google-drive": "drive.google.com",
  "google-meet": "meet.google.com", "google-workspace": "workspace.google.com",
  "google-maps": "maps.google.com", "google-ads": "ads.google.com", "google-ai": "gemini.google.com",
  "google-analytics": "analytics.google.com", "google-cloud": "cloud.google.com",
  instagram: "instagram.com", youtube: "youtube.com", "youtube-music": "music.youtube.com",
  facebook: "facebook.com", "meta-ads": "business.facebook.com", canva: "canva.com",
  outlook: "outlook.live.com", onenote: "onenote.com", "microsoft-to-do": "to-do.office.com",
};

// Colored CDN first (see getLogoCandidates); jsDelivr mono SVG is a fallback.
const SIMPLE_ICONS_CDN = "https://cdn.jsdelivr.net/npm/simple-icons@latest/icons";
const COLORED_ICONS_CDN = "https://cdn.simpleicons.org";
// Brand hex colors (no #) for coloured logos. Slug -> hex.
const ICON_HEX = {
  slack: "4A154B", spotify: "1DB954", amazon: "FF9900", google: "4285F4", googlemaps: "4285F4",
  gmail: "EA4335",
  segment: "0F1EFF", amplitude: "00D9F5", mixpanel: "7856FF", notion: "000000", figma: "F24E1E",
  canva: "00C4CC", miro: "050038", trello: "0052CC", airtable: "18BFFF", dropbox: "0061FF",
  todoist: "E44332", github: "181717", stripe: "635BFF", paypal: "00457C", instagram: "E4405F",
  facebook: "1877F2", x: "000000", linkedin: "0A66C2", reddit: "FF4500", twitch: "9146FF",
  youtube: "FF0000", vimeo: "1AB7EA", zoom: "0B5CFF", discord: "5865F2", telegram: "26A5E4",
  twilio: "F22F46", vercel: "000000", netlify: "00C7B7", supabase: "3ECF8E", firebase: "FFCA28",
  docker: "2496ED", openai: "412991", anthropic: "D4A574", huggingface: "FFD21E",
  shopify: "7AB55C", zapier: "FF4A00", make: "0C0C0C", n8n: "FF6D5A", mailchimp: "FFE01B",
  hubspot: "FF7A59", salesforce: "00A1E0", zendesk: "03363D", calendly: "006BFF", docusign: "FFCC22",
  hootsuite: "000000", buffer: "000000", asana: "F06A6A", monday: "FF3D57", microsoft: "5E5E5E",
  applemusic: "FA243C", soundcloud: "FF5500", deezer: "FEAA2D", pandora: "3668FF", tidal: "000000",
  tripadvisor: "34E0A1", bookingdotcom: "003580", airbnb: "FF5A5F", skyscanner: "0D3E88",
  expedia: "FFCB00", linear: "5E6AD2", ticktick: "E44332", coda: "F46A54", obsidian: "7C3AED",
  googlecalendar: "4285F4", googledrive: "4285F4", googledocs: "4285F4", googlesheets: "0F9D58",
  clickup: "7B68EE", basecamp: "1D2D35", microsoftoutlook: "0078D4", microsoftteams: "6264A7",
  whatsapp: "25D366", cisco: "049FD9", jira: "0052CC", confluence: "172B4D",
  cloudflare: "F38020", auth0: "EB5424", wix: "0C6EFC", wordpress: "21759B", webflow: "4353FF",
  contentful: "2478CC", sanity: "F03E2F", googleanalytics: "E37400", meta: "0668E1", googleads: "4285F4",
  replit: "F26207", etsy: "F16521", gumroad: "FF90AE", mattermost: "0058CC", ifttt: "000000",
  paddle: "0066CC", dribbble: "EA4C89", behance: "1769FF", bluesky: "0085FF", mastodon: "6364FF",
  substack: "FF6719", getpocket: "EF3F56", patreon: "FF424D", kofi: "FF5E5B", tumblr: "36465D",
  googlecloud: "4285F4", microsoftazure: "0078D4", heroku: "430098", digitalocean: "0080FF",
  mongodb: "47A248", amazonaws: "FF9900", sentry: "362D59", circleci: "343434", terraform: "7B42BC",
  railway: "0B0D0E", flyio: "0F1824", render: "46E3B7", ebay: "E53238", hashnode: "2962FF",
  devdotto: "0A0A0A", ghost: "15171A", raindropio: "3DB2FF", rocketdotchat: "F5455C", zulip: "1F7EC7",
  nuclino: "0066FF", whereby: "0B66D3", lark: "00D6B5", pipedrive: "017B45", twist: "000000",
  copper: "C87533", craft: "000000", slite: "1E1E1E", readwise: "00D97E", customerio: "FF692E",
  resend: "000000", assemblyai: "000000", wise: "9FE870", square: "006AFF", adyen: "0ABF53",
  braintree: "019CD6", sproutsocial: "2AB22A", evernote: "00A82D", smartsheet: "2ECC71", wrike: "69BE00",
  quora: "B92B27", signal: "3A76F0", klaviyo: "E9343F", convertkit: "FB697F", braze: "EE2C6C",
  framer: "0055FF", typeform: "262627", freshbooks: "E64B50", quickbooks: "2CA01C", xero: "13B5EA",
  activecampaign: "0E9F5B", getresponse: "00B368", brevo: "0696D7", mollie: "000000", gocardless: "0A4B78",
  razorpay: "0C2451", viator: "000000", uber: "000000", lyft: "FF00BF", cohere: "BC5228",
  mistralai: "000000", stability: "775FFB", replicate: "000000", elevenlabs: "000000", pinecone: "4200FF",
  adyen: "0ABF53", braintree: "019CD6", evernote: "00A82D", quora: "B92B27", signal: "3A76F0",
  smartsheet: "2ECC71", wrike: "69BE00", sproutsocial: "2AB22A", microsoftonenote: "7719AA",
  googlemeet: "00897B",
};
function faviconUrl(domain) {
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;
}

/** Derive a brand hostname from an API/docs URL when APP_LOGO_DOMAINS is missing. */
function domainFromApiUrl(apiUrl) {
  if (!apiUrl) return null;
  try {
    let host = new URL(apiUrl).hostname.replace(/^www\./i, "").toLowerCase();
    // developer.wrike.com → wrike.com; docs.github.com stays github.com after strip
    const stripped = host.replace(
      /^(?:developer|developers|developer-api|api|apis|docs|doc|help|support|learn|platform|console|cloud)\./i,
      ""
    );
    if (stripped && stripped.includes(".")) host = stripped;
    // microsoft learn / adobe developer hosts → brand domains
    if (host.endsWith("microsoft.com") || host === "microsoft.com") return "microsoft.com";
    if (host.endsWith("adobe.com") || host === "adobe.com") return "adobe.com";
    if (host.endsWith("google.com") || host === "google.com") return "google.com";
    return host;
  } catch (_) {
    return null;
  }
}

/**
 * Ordered logo candidates (color first):
 * local (Commons SVG / brand favicon) → colored Simple Icons (slug+hex, then slug)
 * → mapped domain favicon → apiUrl favicon → id.com guess → jsDelivr mono SVG last.
 * Favicons must come before mono SVG: many brands 404 on cdn.simpleicons.org
 * (Slack, Canva, Outlook, …) and a successful black jsDelivr load would otherwise
 * permanently skip colorful favicons.
 */
function getLogoCandidates(app) {
  const id = app?.id ? String(app.id) : "";
  const out = [];
  const push = (url) => {
    if (url && !out.includes(url)) out.push(url);
  };

  if (id && APP_LOGO_LOCAL[id]) push(APP_LOGO_LOCAL[id]);

  const slug = id && APP_LOGO_ICONS[id];
  if (slug) {
    const hex = ICON_HEX[slug];
    // Brand-colored CDN — preferred for mapped apps without a local mark
    if (hex) push(`${COLORED_ICONS_CDN}/${slug}/${hex}`);
    push(`${COLORED_ICONS_CDN}/${slug}`);
  }

  if (id && APP_LOGO_DOMAINS[id]) push(faviconUrl(APP_LOGO_DOMAINS[id]));
  push(faviconUrl(domainFromApiUrl(app?.apiUrl)));

  // Last-resort brand guess from id (e.g. wrike → wrike.com)
  if (id && !id.includes(".") && id.length > 1) {
    const guess = `${id.replace(/-/g, "")}.com`;
    push(faviconUrl(guess));
  }

  // Mono SVG last — loads reliably but is black/gray; never prefer over color sources
  if (slug) push(`${SIMPLE_ICONS_CDN}/${slug}.svg`);

  return out;
}

function getLogoUrl(app) {
  const list = getLogoCandidates(app);
  return list[0] || null;
}

function AppLogo({ app, size = 28 }) {
  const candidates = useMemo(
    () => getLogoCandidates(app),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-resolve when identity/url changes
    [app?.id, app?.apiUrl]
  );
  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setSourceIndex(0);
  }, [app?.id, app?.apiUrl]);

  const exhausted = sourceIndex >= candidates.length;
  const src = exhausted ? null : candidates[sourceIndex];
  const initials = (app.name || "?")
    .replace(/\s*\([^)]*\)\s*/g, "")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  if (!src) {
    return (
      <span className="apps-logo-fallback" style={{ width: size, height: size, fontSize: size * 0.4 }}>
        {initials}
      </span>
    );
  }

  return (
    <img
      key={`${app?.id || "app"}-${sourceIndex}`}
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      className="apps-logo-img"
      style={{ width: size, height: size }}
      onError={() => setSourceIndex((i) => i + 1)}
    />
  );
}

const FALLBACK_CATEGORIES = [
  { category: "Creative & design", apps: [{ name: "Photoshop", apiUrl: "https://developer.adobe.com/photoshop/", id: "photoshop", hasOAuth: true }, { name: "Illustrator", apiUrl: "https://developer.adobe.com/illustrator/", id: "illustrator", hasOAuth: true }, { name: "Canva", apiUrl: "https://www.canva.com/developers/", id: "canva", hasOAuth: true }, { name: "Figma", apiUrl: "https://www.figma.com/developers/api", id: "figma", hasOAuth: true }, { name: "Miro", apiUrl: "https://developers.miro.com/docs/rest-api-overview", id: "miro", hasOAuth: true }, { name: "Sketch", apiUrl: "https://developer.sketch.com/", id: "sketch", hasOAuth: true }] },
  { category: "Video & media", apps: [{ name: "Premiere Pro", apiUrl: "https://developer.adobe.com/premiere-pro/", id: "premiere", hasOAuth: true }] },
  { category: "Real estate", apps: [{ name: "Zillow", apiUrl: "https://www.zillow.com/developers/", id: "zillow", hasOAuth: false }] },
  { category: "Music & audio", apps: [
    { name: "Spotify", apiUrl: "https://developer.spotify.com/documentation/web-api", id: "spotify", hasOAuth: true }, { name: "Apple Music", apiUrl: "https://developer.apple.com/documentation/applemusicapi", id: "apple-music", hasOAuth: true },
    { name: "YouTube Music", apiUrl: "https://developers.google.com/youtube/v3", id: "youtube-music", hasOAuth: true }, { name: "SoundCloud", apiUrl: "https://developers.soundcloud.com/docs/api/guide", id: "soundcloud", hasOAuth: true }, { name: "Deezer", apiUrl: "https://developers.deezer.com/api", id: "deezer", hasOAuth: true }, { name: "Pandora", apiUrl: "https://developer.pandora.com/", id: "pandora", hasOAuth: true }, { name: "Amazon Music", apiUrl: "https://developer.amazon.com/music", id: "amazon-music", hasOAuth: true }
  ] },
  { category: "Travel", apps: [{ name: "TripAdvisor", apiUrl: "https://developer-tripadvisor.com/content-api/", id: "tripadvisor", hasOAuth: false }, { name: "Booking.com", apiUrl: "https://developers.booking.com/", id: "booking", hasOAuth: true }, { name: "Airbnb", apiUrl: "https://www.airbnb.com/partner", id: "airbnb", hasOAuth: true }, { name: "Google Maps", apiUrl: "https://developers.google.com/maps/documentation", id: "google-maps", hasOAuth: true }, { name: "Skyscanner", apiUrl: "https://developers.skyscanner.net/docs", id: "skyscanner", hasOAuth: false }, { name: "Hopper", apiUrl: "https://docs.hopper.com/", id: "hopper", hasOAuth: true }] },
  { category: "Productivity", apps: [{ name: "Google Workspace", apiUrl: "https://developers.google.com/workspace", id: "google-workspace", hasOAuth: true }, { name: "Notion", apiUrl: "https://developers.notion.com/", id: "notion", hasOAuth: true }, { name: "Slack", apiUrl: "https://api.slack.com/", id: "slack", hasOAuth: true }, { name: "Trello", apiUrl: "https://developer.atlassian.com/cloud/trello/rest/", id: "trello", hasOAuth: true }, { name: "Airtable", apiUrl: "https://airtable.com/developers/web/api/introduction", id: "airtable", hasOAuth: true }, { name: "Dropbox", apiUrl: "https://www.dropbox.com/developers/documentation", id: "dropbox", hasOAuth: true }, { name: "Todoist", apiUrl: "https://developer.todoist.com/rest/v2", id: "todoist", hasOAuth: true }, { name: "TickTick", apiUrl: "https://developer.ticktick.com/docs", id: "ticktick", hasOAuth: true }, { name: "Coda", apiUrl: "https://coda.io/developers/apis/v1", id: "coda", hasOAuth: true }, { name: "Obsidian", apiUrl: "https://docs.obsidian.md/", id: "obsidian", hasOAuth: false }, { name: "Google Calendar", apiUrl: "https://developers.google.com/calendar/api", id: "google-calendar", hasOAuth: true }, { name: "Google Drive", apiUrl: "https://developers.google.com/drive/api", id: "google-drive", hasOAuth: true }, { name: "Linear", apiUrl: "https://developers.linear.app/docs/graphql/working-with-the-graphql-api", id: "linear", hasOAuth: true }] },
  { category: "Social", apps: [{ name: "Instagram", apiUrl: "https://developers.facebook.com/docs/instagram-api", id: "instagram", hasOAuth: true }, { name: "Facebook", apiUrl: "https://developers.facebook.com/docs/graph-api", id: "facebook", hasOAuth: true }, { name: "X (Twitter)", apiUrl: "https://developer.twitter.com/en/docs/twitter-api", id: "twitter", hasOAuth: true }, { name: "LinkedIn", apiUrl: "https://learn.microsoft.com/en-us/linkedin/", id: "linkedin", hasOAuth: true }, { name: "Reddit", apiUrl: "https://www.reddit.com/dev/api", id: "reddit", hasOAuth: true }, { name: "Twitch", apiUrl: "https://dev.twitch.tv/docs/api/", id: "twitch", hasOAuth: true }, { name: "YouTube", apiUrl: "https://developers.google.com/youtube/v3", id: "youtube", hasOAuth: true }, { name: "Vimeo", apiUrl: "https://developer.vimeo.com/api", id: "vimeo", hasOAuth: true }, { name: "Medium", apiUrl: "https://github.com/Medium/medium-api-docs", id: "medium", hasOAuth: true }] },
  { category: "Communication", apps: [{ name: "Gmail", apiUrl: "https://developers.google.com/gmail/api", id: "gmail", hasOAuth: true }, { name: "Outlook", apiUrl: "https://learn.microsoft.com/en-us/graph/api/resources/mail-api-overview", id: "outlook", hasOAuth: true }, { name: "Zoom", apiUrl: "https://developers.zoom.us/docs/api/", id: "zoom", hasOAuth: true }, { name: "Discord", apiUrl: "https://discord.com/developers/docs/intro", id: "discord", hasOAuth: true }, { name: "Telegram", apiUrl: "https://core.telegram.org/bots/api", id: "telegram", hasOAuth: false }, { name: "Twilio", apiUrl: "https://www.twilio.com/docs/usage/api", id: "twilio", hasOAuth: false }, { name: "Webex", apiUrl: "https://developer.webex.com/docs/api", id: "webex", hasOAuth: true }] },
  { category: "Development & no-code", apps: [{ name: "Lovable", apiUrl: "https://docs.lovable.dev/", id: "lovable", hasOAuth: false }, { name: "Vercel", apiUrl: "https://vercel.com/docs/rest-api", id: "vercel", hasOAuth: true }, { name: "GitHub", apiUrl: "https://docs.github.com/en/rest", id: "github", hasOAuth: true }, { name: "GitLab", apiUrl: "https://docs.gitlab.com/ee/api/", id: "gitlab", hasOAuth: true }, { name: "Netlify", apiUrl: "https://docs.netlify.com/api/", id: "netlify", hasOAuth: true }, { name: "Supabase", apiUrl: "https://supabase.com/docs/reference/javascript/introduction", id: "supabase", hasOAuth: true }, { name: "Firebase", apiUrl: "https://firebase.google.com/docs/reference", id: "firebase", hasOAuth: true }, { name: "AWS", apiUrl: "https://docs.aws.amazon.com/", id: "aws", hasOAuth: false }, { name: "Docker", apiUrl: "https://docs.docker.com/engine/api/", id: "docker", hasOAuth: false }] },
  { category: "Payments", apps: [{ name: "Stripe", apiUrl: "https://stripe.com/docs/api", id: "stripe", hasOAuth: false }, { name: "PayPal", apiUrl: "https://developer.paypal.com/docs/api/overview/", id: "paypal", hasOAuth: true }, { name: "Square", apiUrl: "https://developer.squareup.com/reference/square", id: "square", hasOAuth: true }, { name: "Wise", apiUrl: "https://api-docs.wise.com/", id: "wise", hasOAuth: true }] },
  { category: "Marketing & CRM", apps: [{ name: "Mailchimp", apiUrl: "https://mailchimp.com/developer/marketing/api/", id: "mailchimp", hasOAuth: true }, { name: "SendGrid", apiUrl: "https://docs.sendgrid.com/api-reference/", id: "sendgrid", hasOAuth: false }, { name: "HubSpot", apiUrl: "https://developers.hubspot.com/docs/api/overview", id: "hubspot", hasOAuth: true }, { name: "Salesforce", apiUrl: "https://developer.salesforce.com/docs/apis", id: "salesforce", hasOAuth: true }, { name: "Zendesk", apiUrl: "https://developer.zendesk.com/api-reference/", id: "zendesk", hasOAuth: true }, { name: "Calendly", apiUrl: "https://developer.calendly.com/api-docs", id: "calendly", hasOAuth: true }, { name: "DocuSign", apiUrl: "https://developers.docusign.com/docs/esign-rest-api/", id: "docusign", hasOAuth: true }, { name: "Hootsuite", apiUrl: "https://developer.hootsuite.com/docs", id: "hootsuite", hasOAuth: true }, { name: "Buffer", apiUrl: "https://buffer.com/developers/api", id: "buffer", hasOAuth: true }] },
  { category: "AI & data", apps: [{ name: "OpenAI", apiUrl: "https://platform.openai.com/docs/api-reference", id: "openai", hasOAuth: false }, { name: "Anthropic", apiUrl: "https://docs.anthropic.com/en/api", id: "anthropic", hasOAuth: false }, { name: "Google AI", apiUrl: "https://ai.google.dev/docs", id: "google-ai", hasOAuth: false }, { name: "Hugging Face", apiUrl: "https://huggingface.co/docs/api-inference", id: "huggingface", hasOAuth: true }, { name: "Nano Banana", apiUrl: "https://fal.ai/models/fal-ai/nano-banana-2", id: "nano-banana", hasOAuth: false }, { name: "Veo 3 (VO3)", apiUrl: "https://fal.ai/models/fal-ai/veo3", id: "veo3", hasOAuth: false }] },
  { category: "E-commerce", apps: [{ name: "Shopify", apiUrl: "https://shopify.dev/docs/api", id: "shopify", hasOAuth: true }, { name: "WooCommerce", apiUrl: "https://woocommerce.github.io/woocommerce-rest-api-docs/", id: "woocommerce", hasOAuth: true }, { name: "BigCommerce", apiUrl: "https://developer.bigcommerce.com/docs/rest-management", id: "bigcommerce", hasOAuth: true }] },
  { category: "Automation", apps: [{ name: "Zapier", apiUrl: "https://developers.zapier.com/docs/api/v2/introduction", id: "zapier", hasOAuth: true }, { name: "Make", apiUrl: "https://www.make.com/en/api-documentation", id: "make", hasOAuth: true }, { name: "n8n", apiUrl: "https://docs.n8n.io/api/", id: "n8n", hasOAuth: true }] },
  { category: "Analytics", apps: [{ name: "Google Analytics", apiUrl: "https://developers.google.com/analytics/devguides/reporting", id: "google-analytics", hasOAuth: true }, { name: "Segment", apiUrl: "https://segment.com/docs/connections/sources/catalog/libraries/server/http-api/", id: "segment", hasOAuth: true }, { name: "Mixpanel", apiUrl: "https://developer.mixpanel.com/reference/overview", id: "mixpanel", hasOAuth: false }, { name: "Amplitude", apiUrl: "https://developers.amplitude.com/docs", id: "amplitude", hasOAuth: false }] },
  { category: "Websites & CMS", apps: [{ name: "Wix", apiUrl: "https://dev.wix.com/docs/rest/articles/getting-started", id: "wix", hasOAuth: true }, { name: "WordPress", apiUrl: "https://developer.wordpress.org/rest-api/", id: "wordpress", hasOAuth: true }, { name: "Webflow", apiUrl: "https://developers.webflow.com/", id: "webflow", hasOAuth: true }, { name: "Contentful", apiUrl: "https://www.contentful.com/developers/docs/references/content-management-api/", id: "contentful", hasOAuth: true }, { name: "Sanity", apiUrl: "https://www.sanity.io/docs/api", id: "sanity", hasOAuth: true }] },
  { category: "Advertising", apps: [{ name: "Meta Ads", apiUrl: "https://developers.facebook.com/docs/marketing-apis", id: "meta-ads", hasOAuth: true }, { name: "Google Ads", apiUrl: "https://developers.google.com/google-ads/api/docs/start", id: "google-ads", hasOAuth: true }] },
];

const APPS_CACHE_KEY = "zolaid-apps-catalog";

function readCachedCategories() {
  try {
    const raw = sessionStorage.getItem(APPS_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length) return parsed;
  } catch (_) {}
  return FALLBACK_CATEGORIES;
}

function readConnectedApps() {
  try {
    const raw = localStorage.getItem("zolaid-connected-apps");
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.map((id) => String(id || "").toLowerCase()).filter(Boolean) : [];
  } catch (_) {
    return [];
  }
}

/** Manus-style capability lines under the app name (what you can do in chat). */
const APP_BLURBS = {
  notion: "Find answers in your Notion workspace and create pages on the fly",
  "google-calendar": "Understand your schedule, manage events, and optimize your time",
  dropbox: "Find files, share links, and take action across your Dropbox",
  slack: "Catch up on team discussions and updates without leaving chat",
  gmail: "Draft replies, search your inbox, and summarize email threads instantly",
  outlook: "Write, search, and manage your Outlook emails seamlessly in chat",
  canva: "Create, review, and edit designs without switching tools",
  figma: "Open files, review designs, and collaborate on Figma projects",
  spotify: "Play, search, and control music while you work",
  "apple-music": "Play and search tracks across your Apple Music library",
  "youtube-music": "Play and search music from YouTube Music",
  github: "Browse repos, issues, and pull requests from chat",
  gitlab: "Manage repos, issues, and merge requests in GitLab",
  zoom: "Pull insights from Zoom meetings and follow up on action items",
  teams: "Summarize Teams threads and keep conversations moving",
  discord: "Read channels, send updates, and manage Discord from chat",
  telegram: "Send messages and manage Telegram chats quickly",
  trello: "Move cards, update boards, and keep projects on track",
  asana: "Track tasks, assign work, and update project status",
  todoist: "Capture tasks, set reminders, and clear your to-do list",
  ticktick: "Plan tasks, habits, and reminders in one place",
  linear: "Track issues, triage bugs, and ship project updates",
  airtable: "Query bases, update records, and keep data in sync",
  "google-drive": "Search files, open docs, and organize your Drive",
  "google-workspace": "Access files, search instantly, and manage docs intelligently",
  "microsoft-365": "Work across Outlook, OneDrive, and Microsoft 365 apps",
  photoshop: "Edit photos, apply enhancements, and prepare assets",
  illustrator: "Create and refine vector graphics for any project",
  miro: "Brainstorm, map ideas, and collaborate on Miro boards",
  sketch: "Open Sketch files and keep design reviews moving",
  premiere: "Edit clips, export cuts, and manage video projects",
  youtube: "Find videos, manage uploads, and summarize channels",
  instagram: "Draft posts, review insights, and manage Instagram",
  facebook: "Create posts and manage your Facebook presence",
  twitter: "Read the feed, draft posts, and engage on X",
  linkedin: "Draft posts and manage your LinkedIn presence",
  reddit: "Search threads, summarize posts, and engage on Reddit",
  stripe: "Check payments, invoices, and customer billing status",
  paypal: "Send, track, and reconcile PayPal payments",
  shopify: "Manage products, orders, and store operations",
  zapier: "Kick off Zapier automations and connect your apps",
  make: "Run Make scenarios and automate multi-step workflows",
  n8n: "Trigger n8n workflows and keep automations in sync",
  hubspot: "Update CRM records and run HubSpot campaigns",
  salesforce: "Look up accounts and update Salesforce records",
  calendly: "Schedule meetings and manage Calendly availability",
  docusign: "Send agreements and track signature status",
  openai: "Use OpenAI models for writing, coding, and analysis",
  anthropic: "Use Claude models for deep reasoning and writing",
  "google-ai": "Use Google AI models for generation and analysis",
  "google-maps": "Search places, get directions, and explore maps",
  booking: "Find stays, compare options, and plan bookings",
  airbnb: "Browse listings and plan stays on Airbnb",
  tripadvisor: "Find places, read reviews, and plan trips",
  skyscanner: "Search flights, compare fares, and spot deals",
  hopper: "Track flights and hotels to book at the right time",
  zillow: "Browse home listings and compare neighborhoods",
  vercel: "Deploy projects and monitor Vercel deployments",
  netlify: "Deploy sites and manage Netlify hosting",
  supabase: "Query tables and manage your Supabase project",
  firebase: "Inspect Firebase data and manage project settings",
  docker: "Work with containers and image workflows",
  aws: "Inspect and manage common AWS resources",
  "google-analytics": "See traffic, conversions, and key metrics",
  mixpanel: "Explore funnels, retention, and product analytics",
  amplitude: "Explore product analytics and user journeys",
  segment: "Manage event pipelines and destination sync",
  mailchimp: "Draft campaigns and track email performance",
  sendgrid: "Send transactional email and check delivery",
  wordpress: "Publish posts and manage WordPress content",
  webflow: "Edit pages and publish Webflow sites",
  wix: "Edit pages and publish your Wix site",
  "meta-ads": "Review Meta ad performance and campaign status",
  "google-ads": "Review Google Ads performance and campaign status",
  "nano-banana": "Generate images with Nano Banana from a prompt",
  veo3: "Generate short videos with Veo 3 from a prompt",
  lovable: "Build and iterate apps with Lovable from chat",
};

function shortenBlurb(text, maxWords = 16) {
  const cleaned = String(text || "")
    .replace(/\s+/g, " ")
    .replace(/[.。]+$/g, "")
    .trim();
  if (!cleaned) return "";
  const first = cleaned.split(/[.!?]/)[0].trim() || cleaned;
  const words = first.split(/\s+/);
  if (words.length <= maxWords) return first;
  return `${words.slice(0, maxWords).join(" ")}…`;
}

function appBlurb(app, category) {
  const id = String(app?.id || "").toLowerCase();
  if (id && APP_BLURBS[id]) return APP_BLURBS[id];

  const fromApi = app?.blurb || app?.capabilities || app?.description;
  if (fromApi) {
    const short = shortenBlurb(fromApi, 16);
    if (short && short.split(/\s+/).length <= 20 && !/[;|]/.test(short)) return short;
  }

  const name = (app?.name || "this app").replace(/\s*\([^)]*\)\s*/g, "").trim();
  const cat = String(category || app?.category || "").toLowerCase();
  if (cat.includes("music") || cat.includes("audio")) return `Play, search, and control ${name} from chat`;
  if (cat.includes("creative") || cat.includes("design")) return `Create, review, and edit designs without switching tools`;
  if (cat.includes("video") || cat.includes("media")) return `Edit clips, manage projects, and export video`;
  if (cat.includes("travel")) return `Plan trips, compare options, and manage bookings`;
  if (cat.includes("communication")) return `Read, write, and manage messages in ${name}`;
  if (cat.includes("social")) return `Draft posts, review activity, and manage your presence`;
  if (cat.includes("payment")) return `Check payments, invoices, and billing status`;
  if (cat.includes("productivity")) return `Organize work, files, and tasks in ${name}`;
  if (cat.includes("development") || cat.includes("no-code")) return `Ship projects and manage ${name} from chat`;
  if (cat.includes("marketing") || cat.includes("crm")) return `Update CRM records and run campaigns`;
  if (cat.includes("e-commerce")) return `Manage products, orders, and store operations`;
  if (cat.includes("automation")) return `Trigger automations and keep workflows in sync`;
  if (cat.includes("analytics")) return `Explore metrics, funnels, and product insights`;
  if (cat.includes("ai") || cat.includes("data")) return `Use ${name} for generation, analysis, and tools`;
  if (cat.includes("website") || cat.includes("cms")) return `Publish content and manage your site`;
  if (cat.includes("advertising")) return `Review ad performance and campaign status`;
  if (cat.includes("real estate")) return `Browse listings and compare neighborhoods`;
  return `Use ${name} in chat to get work done faster`;
}

const FEATURED_APP_IDS = [
  "notion",
  "google-calendar",
  "dropbox",
  "slack",
  "gmail",
  "spotify",
  "figma",
  "github",
];

/**
 * @param {object} [props]
 * @param {boolean} [props.embedded] - Render inside chatbot shell (no marketing header/footer)
 * @param {(app: object, connectedIds: string[]) => void} [props.onAppAdded]
 * @param {() => void} [props.onBackToChat]
 */
export default function Apps({ embedded = false, onAppAdded = null, onBackToChat = null } = {}) {
  const [byCategory, setByCategory] = useState(readCachedCategories);
  const [spotifyAuthLoading, setSpotifyAuthLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [connectedApps, setConnectedApps] = useState(readConnectedApps);
  const [activeFilter, setActiveFilter] = useState("all"); // all | connected | category name
  const searchInputRef = React.useRef(null);
  const appsMainRef = React.useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("spotify_connected");
    const err = params.get("spotify_error");
    if (connected === "1") {
      setMessage({ type: "success", text: "Spotify connected successfully." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (err) {
      setMessage({ type: "error", text: `Spotify: ${decodeURIComponent(err)}` });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/apps`, { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok) return;
        const next = Array.isArray(data.byCategory) && data.byCategory.length ? data.byCategory : null;
        if (!next) return;
        setByCategory(next);
        try { sessionStorage.setItem(APPS_CACHE_KEY, JSON.stringify(next)); } catch (_) {}
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Apps page head: title and description (list key apps for SEO / sharing)
  useEffect(() => {
    if (embedded) {
      document.title = "Apps | Zolaid";
      return undefined;
    }
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]');
    const prevDescContent = prevDesc ? prevDesc.getAttribute("content") : null;
    document.title = "Zolaid Apps | Browse and connect your favorite apps";
    if (prevDesc) {
      prevDesc.setAttribute(
        "content",
        "Zolaid Apps – Browse and connect your favorite apps: Canva, Photoshop, Premiere Pro, Zillow, Spotify, Notion, Slack, Trello, Gmail, Zoom, GitHub, Stripe, Shopify, Zapier, and 100+ more. One assistant, many integrations."
      );
    }
    return () => {
      document.title = prevTitle;
      if (prevDesc && prevDescContent !== null) prevDesc.setAttribute("content", prevDescContent);
    };
  }, [embedded]);

  // Keep chat theme token available if other chrome reads it
  useEffect(() => {
    const prev = document.documentElement.dataset.chatTheme;
    try {
      const t = localStorage.getItem("zolaid/chatTheme");
      document.documentElement.dataset.chatTheme =
        typeof t === "string" ? JSON.parse(t) : undefined;
    } catch (e) {}
    return () => {
      if (prev !== undefined) {
        document.documentElement.dataset.chatTheme = prev;
      } else {
        delete document.documentElement.dataset.chatTheme;
      }
    };
  }, []);

  useEffect(() => {
    if (embedded) {
      const el = appsMainRef.current;
      if (!el) return undefined;
      const onScroll = () => setShowBackToTop(el.scrollTop > 320);
      el.addEventListener("scroll", onScroll, { passive: true });
      return () => el.removeEventListener("scroll", onScroll);
    }
    const onScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [embedded]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "/" && (e.target === document.body || !/^(input|textarea|select)$/i.test(e.target?.tagName))) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleConnectSpotify = async () => {
    setSpotifyAuthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/apps/spotify/auth-url`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (data.authUrl) {
        window.location.href = data.authUrl;
        return;
      }
      setMessage({ type: "error", text: data.error || "Could not get Spotify auth URL" });
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Request failed" });
    } finally {
      setSpotifyAuthLoading(false);
    }
  };

  const { t } = useTranslation();

  const sortedCategories = useMemo(() => {
    const order = ["Productivity", "Creative & design", "Video & media", "Music & audio", "Travel", "Real estate", "Communication", "Social"];
    return [...byCategory].sort((a, b) => {
      const i = order.indexOf(a.category);
      const j = order.indexOf(b.category);
      if (i === -1 && j === -1) return (a.category || "").localeCompare(b.category || "");
      if (i === -1) return 1;
      if (j === -1) return -1;
      return i - j;
    });
  }, [byCategory]);

  const allAppsFlat = useMemo(() => {
    const out = [];
    sortedCategories.forEach(({ category, apps }) => {
      (apps || []).forEach((app) => out.push({ ...app, category }));
    });
    return out;
  }, [sortedCategories]);

  const featuredApps = useMemo(() => {
    const byId = new Map(allAppsFlat.map((app) => [String(app.id || "").toLowerCase(), app]));
    return FEATURED_APP_IDS.map((id) => byId.get(id)).filter(Boolean);
  }, [allAppsFlat]);

  const { filteredByCategory, totalApps, resultCount } = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    const totalApps = allAppsFlat.length;
    let working = sortedCategories;

    if (activeFilter === "connected") {
      working = sortedCategories
        .map(({ category, apps }) => ({
          category,
          apps: (apps || []).filter((app) => connectedApps.includes(String(app.id || "").toLowerCase())),
        }))
        .filter((c) => c.apps.length > 0);
    } else if (activeFilter !== "all") {
      working = sortedCategories.filter((c) => c.category === activeFilter);
    }

    if (!q) {
      const resultCount = working.reduce((n, c) => n + (c.apps?.length || 0), 0);
      return { filteredByCategory: working, totalApps, resultCount };
    }

    const filtered = working
      .map(({ category, apps }) => ({
        category,
        apps: (apps || []).filter(
          (app) =>
            (app.name && app.name.toLowerCase().includes(q)) ||
            (app.id && app.id.toLowerCase().replace(/-/g, " ").includes(q)) ||
            (category && category.toLowerCase().includes(q)) ||
            appBlurb(app, category).toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.apps.length > 0);
    const resultCount = filtered.reduce((n, c) => n + c.apps.length, 0);
    return { filteredByCategory: filtered, totalApps, resultCount };
  }, [sortedCategories, allAppsFlat, searchQuery, activeFilter, connectedApps]);

  const addAppAndOpenChat = (app) => {
    const id = String(app?.id || "").toLowerCase();
    const mcp = hasMcpToolPack(id);
    let list = readConnectedApps();
    try {
      if (id && !list.includes(id)) list = [...list, id];
      localStorage.setItem("zolaid-connected-apps", JSON.stringify(list));
      setConnectedApps(list);
      try {
        const mcpList = list.filter((x) => hasMcpToolPack(x));
        localStorage.setItem("zolaid-mcp-apps", JSON.stringify(mcpList));
      } catch (_) {}
    } catch (_) {}

    if (embedded) {
      try { onAppAdded?.(app, list); } catch (_) {}
      try { onBackToChat?.(app); } catch (_) {}
      return;
    }

    const qs = new URLSearchParams();
    if (id) qs.set("connected", id);
    if (mcp) qs.set("mcp", "1");
    window.location.href = `/chatbot?${qs.toString()}`;
  };

  const connectedAppObjects = useMemo(() => {
    const byId = new Map(allAppsFlat.map((app) => [String(app.id || "").toLowerCase(), app]));
    return connectedApps.map((id) => byId.get(id)).filter(Boolean);
  }, [allAppsFlat, connectedApps]);

  const showStarter = !searchQuery.trim() && activeFilter === "all" && featuredApps.length > 0;

  const renderAddButton = (app, connected) => (
    <button
      type="button"
      className={`apps-btn-add${connected ? " is-added" : ""}`}
      onClick={() => addAppAndOpenChat(app)}
      aria-label={connected ? `Open ${app.name} in chat` : `Add ${app.name} to chat`}
      title={connected ? "Open in chat" : "Add to chat"}
    >
      {connected ? (
        <svg className="apps-btn-add-icon" viewBox="0 0 16 16" aria-hidden focusable="false">
          <path
            d="M3.2 8.2l3 3 6.6-6.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <span className="apps-btn-add-plus" aria-hidden>+</span>
      )}
    </button>
  );

  const renderAppItem = (app, category) => {
    const id = String(app.id || "").toLowerCase();
    const connected = connectedApps.includes(id);
    const mcp = hasMcpToolPack(id);
    const blurb = appBlurb(app, category);
    return (
      <li key={`${category}-${id || app.name}`} className={`apps-item${connected ? " is-in-chat" : ""}${mcp ? " has-mcp" : ""}`}>
        <div className="apps-item-mark" aria-hidden>
          <AppLogo app={app} size={36} />
        </div>
        <div className="apps-item-copy">
          <div className="apps-item-name-row">
            <span className="apps-item-name">{app.name}</span>
            {mcp ? (
              <span className="apps-mcp-mark" title="Chat tool pack available">
                tools
              </span>
            ) : null}
          </div>
          <p className="apps-item-blurb">{blurb}</p>
        </div>
        <div className="apps-item-actions">
          {app.id === "spotify" && app.hasOAuth ? (
            <button
              type="button"
              className="apps-btn-connect"
              onClick={handleConnectSpotify}
              disabled={spotifyAuthLoading}
            >
              {spotifyAuthLoading ? "…" : "Connect"}
            </button>
          ) : null}
          {renderAddButton(app, connected)}
        </div>
      </li>
    );
  };

  const searchFieldId = embedded ? "apps-search-embedded" : "apps-search";

  const shell = (
        <div className="apps-shell">
          <header className={`apps-masthead${embedded ? " apps-masthead--compact" : ""}`}>
            <div className="apps-masthead-copy">
              {!embedded ? (
                <p className="apps-wordmark" aria-label="Zolaid">Zolaid</p>
              ) : null}
              <div className="apps-title-row">
                <h1 className="apps-title">{t("apps") || "Apps"}</h1>
                {embedded ? (
                  <p className="apps-meta-inline" aria-live="polite">
                    <span>{totalApps} apps</span>
                    <span aria-hidden>·</span>
                    <span>{connectedApps.length} in chat</span>
                  </p>
                ) : null}
              </div>
              <p className="apps-deck">
                {embedded
                  ? "Add a tool, then ask for it in chat."
                  : "Bring the tools you already run into one assistant. Add an integration, then ask for it while you work."}
              </p>
            </div>
            {!embedded ? (
              <aside className="apps-masthead-aside" aria-live="polite">
                <div className="apps-stat-block">
                  <strong>{totalApps}</strong>
                  <span>apps</span>
                </div>
                <div className="apps-stat-block">
                  <strong>{connectedApps.length}</strong>
                  <span>in your chat</span>
                </div>
              </aside>
            ) : null}
          </header>

          <div className={`apps-search-bar${embedded ? " apps-search-bar--sticky" : ""}`}>
            <label htmlFor={searchFieldId} className="sr-only">Search apps</label>
            <svg className="apps-search-glyph" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.2-3.2" strokeLinecap="round" />
            </svg>
            <input
              ref={searchInputRef}
              id={searchFieldId}
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={embedded ? "Search apps" : "Find Slack, Figma, Stripe…"}
              aria-label="Search apps by name or category"
              autoComplete="off"
            />
            <div className="apps-search-tools">
              {searchQuery ? (
                <button type="button" className="apps-search-clear" onClick={() => setSearchQuery("")}>
                  Clear
                </button>
              ) : embedded ? null : (
                <span className="apps-search-hint">Press <kbd>/</kbd></span>
              )}
            </div>
          </div>

          {connectedAppObjects.length > 0 ? (
            <section className="apps-kit" aria-label="Apps already in your chat">
              <div className="apps-kit-label">
                <span>Your kit</span>
                <button type="button" className="apps-kit-link" onClick={() => setActiveFilter("connected")}>
                  View all
                </button>
              </div>
              <ul className="apps-kit-rail">
                {connectedAppObjects.slice(0, 12).map((app) => (
                  <li key={app.id}>
                    <button
                      type="button"
                      className="apps-kit-chip"
                      onClick={() => addAppAndOpenChat(app)}
                      title={`Open ${app.name} in chat`}
                    >
                      <AppLogo app={app} size={22} />
                      <span>{app.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <div className="apps-layout">
            <nav className="apps-rail" aria-label="Browse by category">
              <p className="apps-rail-label">Browse</p>
              <button
                type="button"
                className={`apps-rail-item${activeFilter === "all" ? " is-active" : ""}`}
                onClick={() => setActiveFilter("all")}
              >
                Everything
              </button>
              <button
                type="button"
                className={`apps-rail-item${activeFilter === "connected" ? " is-active" : ""}`}
                onClick={() => setActiveFilter("connected")}
              >
                In your chat
                {connectedApps.length ? <em>{connectedApps.length}</em> : null}
              </button>
              <div className="apps-rail-sep" aria-hidden />
              {sortedCategories.map(({ category, apps }) => (
                <button
                  key={category}
                  type="button"
                  className={`apps-rail-item${activeFilter === category ? " is-active" : ""}`}
                  onClick={() => setActiveFilter(category)}
                >
                  {category}
                  <em>{apps?.length || 0}</em>
                </button>
              ))}
            </nav>

            <div className="apps-stream">
              {message ? (
                <div
                  role="alert"
                  className={`apps-message ${message.type === "success" ? "apps-message-success" : "apps-message-error"}`}
                >
                  {message.text}
                </div>
              ) : null}

              {searchQuery.trim() ? (
                <p className="apps-result-line" aria-live="polite">
                  {resultCount === 0
                    ? `Nothing matches “${searchQuery.trim()}”`
                    : `${resultCount} match${resultCount === 1 ? "" : "es"}`}
                </p>
              ) : null}

              {showStarter ? (
                <section className={`apps-starter${embedded ? " apps-band--shell" : ""}`} aria-labelledby="apps-starter-heading">
                  <div className="apps-band-head">
                    {!embedded ? <span className="apps-band-index" aria-hidden>01</span> : null}
                    <div>
                      <h2 id="apps-starter-heading">
                        {embedded ? "Popular" : "Start here"}
                        {embedded ? <span className="apps-band-chevron" aria-hidden>›</span> : null}
                      </h2>
                      {!embedded ? <p>Common tools people wire into Zolaid first.</p> : null}
                    </div>
                  </div>
                  <ul className="apps-starter-track">
                    {featuredApps.map((app) => {
                      const connected = connectedApps.includes(String(app.id || "").toLowerCase());
                      return (
                        <li key={app.id} className={`apps-starter-tile${connected ? " is-in-chat" : ""}`}>
                          <div className="apps-starter-top">
                            <div className="apps-starter-logo" aria-hidden>
                              <AppLogo app={app} size={40} />
                            </div>
                            {renderAddButton(app, connected)}
                          </div>
                          <strong>{app.name}</strong>
                          <span>{appBlurb(app, app.category)}</span>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ) : null}

              {byCategory.length === 0 ? (
                <div className="apps-empty-state">
                  <p>No app data available.</p>
                </div>
              ) : null}

              {filteredByCategory.length === 0 && (searchQuery.trim() || activeFilter !== "all") ? (
                <div className="apps-empty-state">
                  <p>
                    {activeFilter === "connected"
                      ? "Nothing in your kit yet — pick an app and tap +."
                      : <>No apps match{searchQuery.trim() ? <> <span className="apps-empty-query">“{searchQuery.trim()}”</span></> : null}.</>}
                  </p>
                </div>
              ) : null}

              {filteredByCategory.map(({ category, apps }, index) => {
                const bandIndex = String((showStarter ? index + 2 : index + 1)).padStart(2, "0");
                const catId = `apps-cat-${category.replace(/\s+/g, "-").toLowerCase()}`;
                return (
                  <section key={category} className={`apps-band${embedded ? " apps-band--shell" : ""}`} aria-labelledby={catId}>
                    <div className="apps-band-head">
                      {!embedded ? (
                        <span className="apps-band-index" aria-hidden>{bandIndex}</span>
                      ) : null}
                      <div>
                        <h2 id={catId}>
                          {category}
                          {embedded ? <span className="apps-band-chevron" aria-hidden>›</span> : null}
                        </h2>
                        {!embedded ? (
                          <p>{apps.length} integration{apps.length === 1 ? "" : "s"} you can call from chat.</p>
                        ) : null}
                      </div>
                    </div>
                    <ul className={`apps-item-list${embedded ? " apps-item-list--grid" : ""}`}>
                      {apps.map((app) => renderAppItem(app, category))}
                    </ul>
                  </section>
                );
              })}

              <footer className="apps-footer-cta">
                {embedded ? (
                  <div className="apps-footer-actions apps-footer-actions--shell">
                    <button type="button" className="apps-open-chat-btn" onClick={() => onBackToChat?.()}>
                      Back to chat
                    </button>
                  </div>
                ) : (
                  <>
                    <p>Missing a tool? Ask in chat — we track requests.</p>
                    <div className="apps-footer-actions">
                      <a href="/chatbot" className="apps-open-chat-btn">Open chat</a>
                      <a href="/chatbot" className="apps-request-link">Request an integration</a>
                    </div>
                  </>
                )}
              </footer>
            </div>
          </div>

          <button
            type="button"
            className="apps-back-to-top"
            onClick={() => {
              if (embedded && appsMainRef.current) {
                appsMainRef.current.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            aria-label="Back to top"
            aria-hidden={!showBackToTop}
            tabIndex={showBackToTop ? 0 : -1}
          >
            ↑
          </button>
        </div>
  );

  if (embedded) {
    return (
      <div className="apps-page apps-page--embedded">
        <main id="main-content" className="apps-main" ref={appsMainRef}>
          {shell}
        </main>
      </div>
    );
  }

  return (
    <div className="apps-page">
      <PageHeader />
      <main id="main-content" className="apps-main" ref={appsMainRef}>
        {shell}
      </main>
      <SiteFooter />
    </div>
  );
}
